import BaseObject from "@shared/BaseObject.js";
import {GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} from "@/core/js/model/SharedConstants";

export default class MeaningfulUsageTracker extends BaseObject {

	constructor(options = {}) {
		super()
		let {
			streakThresholdMs = 20 * 60 * 1000,
			cumulativeThresholdMs = 60 * 60 * 1000,
			idleGapMs = 2 * 60 * 1000,
		} = options

		this.streakThresholdMs = streakThresholdMs
		this.cumulativeThresholdMs = cumulativeThresholdMs
		this.idleGapMs = idleGapMs
		this.reset()
	}

	reset() {
		this.currentItemId = null
		this.streakStartedAt = 0
		this.lastActivityAt = 0
		this.cumulativeActiveMs = 0
		this.hasCrossedStreakThreshold = false
		this.hasCrossedCumulativeThreshold = false
	}

	reportActivity(itemId, now = Date.now()) {
		let signal = null
		if(!itemId){
			this.logDiagnostic("reportActivity skipped: missing itemId", {itemId, now})
			return signal
		}

		this.ensureTrackingItem(itemId)
		this.ensureStreakStarted(now)
		let deltaMs = this.getDeltaMs(now)
		let isIdleGapExceeded = deltaMs > this.idleGapMs
		if(isIdleGapExceeded){
			this.streakStartedAt = now
			deltaMs = 0
		}
		let cappedDeltaMs = this.getCappedDeltaMs(deltaMs)
		this.cumulativeActiveMs += cappedDeltaMs
		this.lastActivityAt = now
		let streakDurationMs = this.getStreakDurationMs(now)
		signal = this.createThresholdCrossedSignalIfNeeded({
			itemId,
			detectedAt: now,
			streakDurationMs,
		})

		this.logDiagnostic("activity reported", {
			itemId,
			now,
			lastActivityAt: this.lastActivityAt,
			deltaMs,
			idleGapMs: this.idleGapMs,
			isIdleGapExceeded,
			cappedDeltaMs,
			streakStartedAt: this.streakStartedAt,
			streakDurationMs,
			streakThresholdMs: this.streakThresholdMs,
			cumulativeActiveMs: this.cumulativeActiveMs,
			cumulativeThresholdMs: this.cumulativeThresholdMs,
			hasCrossedStreakThreshold: this.hasCrossedStreakThreshold,
			hasCrossedCumulativeThreshold: this.hasCrossedCumulativeThreshold,
			didCrossThresholdNow: !!signal,
			crossedThresholdMetric: signal?.metric || null,
		})

		return signal
	}

	ensureTrackingItem(itemId) {
		if(this.currentItemId === itemId){
			return
		}
		this.logDiagnostic("tracking item changed", {
			previousItemId: this.currentItemId,
			nextItemId: itemId,
		})
		this.reset()
		this.currentItemId = itemId
	}

	ensureStreakStarted(now) {
		if(this.streakStartedAt <= 0){
			this.streakStartedAt = now
		}
	}

	createThresholdCrossedSignalIfNeeded({itemId, detectedAt, streakDurationMs}) {
		let signal = null
		if(!this.hasCrossedStreakThreshold && streakDurationMs >= this.streakThresholdMs){
			this.hasCrossedStreakThreshold = true
			signal = this.createThresholdCrossedSignal({
				itemId,
				detectedAt,
				metric: "streak",
				streakDurationMs,
				cumulativeActiveMs: this.cumulativeActiveMs,
			})
		}

		if(!signal && !this.hasCrossedCumulativeThreshold && this.cumulativeActiveMs >= this.cumulativeThresholdMs){
			this.hasCrossedCumulativeThreshold = true
			signal = this.createThresholdCrossedSignal({
				itemId,
				detectedAt,
				metric: "cumulative",
				streakDurationMs,
				cumulativeActiveMs: this.cumulativeActiveMs,
			})
		}

		if(signal){
			this.logDiagnostic("meaningful threshold crossed", signal)
		}
		return signal
	}

	createThresholdCrossedSignal({itemId, detectedAt, metric, streakDurationMs, cumulativeActiveMs}) {
		return {
			itemId: itemId || this.currentItemId,
			detectedAt,
			metric,
			streakDurationMs,
			cumulativeActiveMs,
		}
	}

	getDeltaMs(now) {
		if(this.lastActivityAt <= 0){
			return 0
		}
		return Math.max(0, now - this.lastActivityAt)
	}

	getCappedDeltaMs(deltaMs) {
		return Math.min(deltaMs, this.idleGapMs)
	}

	getStreakDurationMs(now) {
		if(this.streakStartedAt <= 0){
			return 0
		}
		return Math.max(0, now - this.streakStartedAt)
	}

	getMeaningfulUsageMap(container, scopeKey) {
		container.meaningfulUsageAtMap = container.meaningfulUsageAtMap || {}
		container.meaningfulUsageAtMap[scopeKey] = container.meaningfulUsageAtMap[scopeKey] || {}
		return container.meaningfulUsageAtMap[scopeKey]
	}

	markMeaningfulUsage(container, scopeKey, itemId, timestampMs = Date.now()) {
		let meaningfulUsageAtMap = this.getMeaningfulUsageMap(container, scopeKey)
		if(!meaningfulUsageAtMap[itemId]){
			meaningfulUsageAtMap[itemId] = timestampMs
		}
		return meaningfulUsageAtMap
	}

	getMeaningfulUsageCount(container, scopeKey) {
		let meaningfulUsageAtMap = this.getMeaningfulUsageMap(container, scopeKey)
		return Object.keys(meaningfulUsageAtMap).length
	}

	logDiagnostic(message, payload = {}) {
		console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} MeaningfulUsageTracker: ${message}`, payload)
	}
}
